import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
// Horizon 4.0.0 migro los eventos de comercio a @shopify/events. Se importa solo eso:
// `VariantPicker` NO se importa estaticamente a proposito — Esprit lo carga bajo demanda
// en ensureProductFormScripts() para no traerlo en PLP (optimizacion propia).
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

const MAX_CACHE_SIZE = 20;

/**
 * Dynamically imports product-form.js and variant-picker.js when on non-product pages.
 * Caches the promise to avoid re-fetching on subsequent calls.
 * @returns {Promise<[unknown, unknown]> | void}
 */
let productFormScriptsPromise = null;

function ensureProductFormScripts() {
  // Already on product page where scripts are already loaded
  if (window.Theme?.template?.name === 'product') return;
  // Return cached promise if already loading/loaded
  if (productFormScriptsPromise) return productFormScriptsPromise;

  productFormScriptsPromise = Promise.all([
    import('@theme/product-form'),
    import('@theme/variant-picker'),
  ]);
  return productFormScriptsPromise;
}

class LRUCache {
  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {number} */
  #maxSize;

  /** @param {number} maxSize */
  constructor(maxSize = MAX_CACHE_SIZE) {
    this.#maxSize = maxSize;
  }

  /** @param {string} key */
  get(key) {
    if (!this.#cache.has(key)) return undefined;
    const value = this.#cache.get(key);
    this.#cache.delete(key);
    this.#cache.set(key, /** @type {string} */ (value));
    return value;
  }

  /** @param {string} key @param {string} value */
  set(key, value) {
    if (this.#cache.has(key)) this.#cache.delete(key);
    else if (this.#cache.size >= this.#maxSize) {
      const oldestKey = this.#cache.keys().next().value;
      if (oldestKey) this.#cache.delete(oldestKey);
    }
    this.#cache.set(key, value);
  }

  has(key) {
    return this.#cache.has(key);
  }

  clear() {
    this.#cache.clear();
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController} Controla los listeners de cart update (patron del vendor 4.0.0) */
  #cartUpdateAbortController = new AbortController();

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {LRUCache} */
  #cache = new LRUCache(MAX_CACHE_SIZE);

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    // H-4.1.4 (H-007): ruta vendor. El early return de arriba ya delega el camino de
    // product-card en `productCard.productPageUrl`, que centraliza la construccion de la
    // URL y es MAS completo que lo que haciamos aca: maneja `set` Y `remove` de la
    // variante (product-card.js:110-124), mientras nuestro codigo inline solo hacia `set`.
    // Queda un unico dueño de la URL en vez de dos implementaciones divergentes.
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cache.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Dynamic import for non-product pages (home, collection, search)
    // On product pages, scripts are already loaded via scripts.liquid
    if (window.Theme?.template?.name !== 'product') {
      await ensureProductFormScripts();
    }

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    // H-4.1.4 (H-008): combinacion. El guard de selling plans es del vendor 4.1.4 y se
    // adopta; el mecanismo de cache es de Esprit y se conserva.
    //
    // El quick-add no sabe renderizar opciones de suscripcion, asi que para esos productos
    // el vendor navega a la ficha en vez de abrir el modal. Su otra mitad (no voltear la
    // etiqueta choose<->add, quick-add.js:143) ya habia entrado por auto-merge: dejar esta
    // afuera dejaba el estado incoherente. El atributo esta vivo, lo computa
    // snippets/quick-add.liquid:54-56 y lo emite en :125.
    //
    // Se omite el `if (currentUrl)` del vendor: la linea de arriba ya retorna si esta vacio.
    if (this.dataset.usesSellingPlans === 'true') {
      window.location.href = currentUrl;
      return;
    }

    let productHtml = this.#cache.get(currentUrl);

    if (!productHtml) {
      productHtml = await this.fetchQuickAddSection(currentUrl);
      if (productHtml) {
        this.#cache.set(currentUrl, productHtml);
      }
    }

    if (productHtml) {
      await this.updateQuickAddModal(productHtml);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the quick add section content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<string | null>}
   */
  async fetchQuickAddSection(productPageUrl) {
    if (!productPageUrl) return null;

    const url = new URL(productPageUrl);
    url.searchParams.set('section_id', 'quick-add-content');

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url.toString(), {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch quick add section: HTTP error ${response.status}`);
      }

      return await response.text();
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the modal content with the fetched HTML
   * @param {string} html - The section HTML
   */
  async updateQuickAddModal(html) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!html || !modalContent) return;

    morph(modalContent, html);

      // El bloque del vendor usa `productGrid`, definido en SU version de este metodo.
      // Esprit hace `morph(modalContent, html)` arriba, asi que view-event-payload viaja
      // dentro del HTML morfeado y no hace falta sincronizarlo aparte.
    requestAnimationFrame(() => {
      modalContent.querySelectorAll('carousel-component').forEach((el) => {
        if (typeof el.reinit === 'function') el.reinit();
      });
    });

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #touchStartX = 0;
  /** @type {number} */
  #touchStartY = 0;
  /** @type {number} */
  #lastTouchY = 0;
  /** @type {number} */
  #lastTouchTime = 0;
  /** @type {number} */
  #velocity = 0;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #dragConfirmed = false;

  /** Minimum distance to trigger close if velocity is low */
  static SWIPE_THRESHOLD = 100;
  /** Minimum velocity (px/ms) to trigger close */
  static VELOCITY_THRESHOLD = 0.6;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Eventos migrados a @shopify/events. Se conserva el listener de DialogCloseEvent, que el
    // vendor omite en su version y que Esprit necesita para #handleDialogClose (fix de iOS).
    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, { signal });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    this.#initDragHandle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #initDragHandle() {
    const dialog = this.refs.dialog;
    const handle = /** @type {HTMLElement | null} */ (this.refs.dragHandle);
    if (!dialog || !handle) return;

    const { signal } = this.#abortController;

    handle.addEventListener('click', () => this.closeDialog(), { signal });

    dialog.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
    dialog.addEventListener('touchmove', this.#onTouchMove, { passive: false, signal });
    dialog.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
  }

  /**
   * @param {TouchEvent} e
   */
  #onTouchStart = (e) => {
    if (!isMobileBreakpoint()) return;

    const touch = e.touches[0];
    if (!touch) return;

    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
    this.#lastTouchY = touch.clientY;
    this.#lastTouchTime = Date.now();
    this.#isDragging = true;
    this.#dragConfirmed = false;
    this.#velocity = 0;
  };

  /**
   * @param {TouchEvent} e
   */
  #onTouchMove = (e) => {
    if (!this.#isDragging || !isMobileBreakpoint()) return;

    const touch = e.touches[0];
    if (!touch) return;

    const deltaY = touch.clientY - this.#touchStartY;
    const deltaX = touch.clientX - this.#touchStartX;
    const currentTime = Date.now();
    const timeDelta = currentTime - this.#lastTouchTime;

    if (timeDelta > 0) {
      this.#velocity = (touch.clientY - this.#lastTouchY) / timeDelta;
    }
    this.#lastTouchY = touch.clientY;
    this.#lastTouchTime = currentTime;

    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    if (!this.#dragConfirmed) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        this.#isDragging = false;
        return;
      }

      if (dialog.scrollTop > 5) {
        return;
      }

      if (deltaY > 5) {
        this.#dragConfirmed = true;
        dialog.style.transition = 'none';
        dialog.style.animation = 'none';
      } else {
        return;
      }
    }

    if (this.#dragConfirmed) {
      e.preventDefault();
      const moveY = Math.max(0, deltaY);
      dialog.style.transform = `translateY(${moveY}px)`;

      const opacity = Math.max(0, 1 - (moveY / (window.innerHeight * 0.5)));
      dialog.style.setProperty('--backdrop-opacity-multiplier', opacity.toString());
    }
  };

  /**
   * @param {TouchEvent} e
   */
  #onTouchEnd = (e) => {
    if (!this.#isDragging || !this.#dragConfirmed) {
      this.#isDragging = false;
      return;
    }

    this.#isDragging = false;
    const dialog = this.refs.dialog;
    if (!(dialog instanceof HTMLElement)) return;

    const deltaY = (e.changedTouches[0]?.clientY ?? 0) - this.#touchStartY;

    if (deltaY > QuickAddDialog.SWIPE_THRESHOLD || this.#velocity > QuickAddDialog.VELOCITY_THRESHOLD) {
      this.#dismissFromDrag();
    } else {
      dialog.style.transition = 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)';
      dialog.style.transform = '';
      dialog.style.removeProperty('--backdrop-opacity-multiplier');
    }
  };

  async #dismissFromDrag() {
    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.#isDragging = false;
    this.#dragConfirmed = false;

    dialog.style.removeProperty('--backdrop-opacity-multiplier');

    await this.closeDialog();

    dialog.style.transform = '';
    dialog.style.transition = '';
    dialog.style.animation = '';
  }

  /**
   * @param {CartUpdateEvent} event
  /**
   * Cierra el dialogo cuando el carrito se actualizo sin error.
   * Migrado a StandardEvents.cartLinesUpdate: el resultado llega por la promesa del evento.
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * Sincroniza galeria y enlaces del modal al cambiar de variante.
   *
   * Migrado de ThemeEvents.variantUpdate a StandardEvents.productSelect. Se conserva el cuerpo
   * de Esprit, que hace mas que el del vendor: ademas de actualizar los enlaces, morfea
   * `.quick-add-content__media` y reinicia los carruseles. Los selectores son los de Esprit
   * (`.quick-add-content__*`), no los del vendor (`.view-product-title`).
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const galleryContainer = /** @type {HTMLElement | null} */ (this.querySelector('.quick-add-content__media'));
        const newGallerySource = html.querySelector('.quick-add-content__media');

        if (galleryContainer && newGallerySource) {
          morph(galleryContainer, newGallerySource);

          requestAnimationFrame(() => {
            galleryContainer.querySelectorAll('carousel-component').forEach((el) => {
              if (typeof el.reinit === 'function') el.reinit();
            });
          });
        }

        const variantPicker = html.querySelector('variant-picker');
        const productUrl = variantPicker?.dataset?.productUrl;
        if (!productUrl) return;

        const variantId = detail?.resource?.id;
        const url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);

        const href = url.pathname + url.search;
        const viewDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-content__view-details'));
        const productTitleLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.quick-add-content__title'));

        if (viewDetailsLink) viewDetailsLink.href = href;
        if (productTitleLink) productTitleLink.href = href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();

    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = /** @type {HTMLElement | null} */ (document.querySelector('#ResultsList [product-grid-view]'));
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
